import React from 'react';
import { CellContext } from '@tanstack/react-table';

import { AdaptiveTableCell } from '@components/common';
import Actions from '@components/common/Actions';

import { isBoolean } from '@utils/common';

import { ETechnologiesValues } from '@enums/common';

import { columnSizes } from '@constants/table';
import { ETechnologyTableFields, ETechnologyTableTitles } from '../constants';

export const getColumns = ({
	technology,
	clearCell,
	editCell,
	openEditCellModal,
	openClearCellModal,
	isEditAccess,
}: {
	technology?: string;
	clearCell: (cellId: number) => void;
	editCell: (cellId: number) => void;
	openEditCellModal: () => void;
	openClearCellModal: () => void;
	isEditAccess?: boolean;
}) => ([
	{
		header: ETechnologyTableTitles.cellId,
		accessorKey: ETechnologyTableFields.cellId,
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: ETechnologyTableTitles.zone,
		accessorKey: ETechnologyTableFields.zone,
		enableSorting: false,
		isHidden: technology === ETechnologiesValues.C_5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.title} />;
		},
	},
	{
		header: ETechnologyTableTitles.name,
		accessorKey: ETechnologyTableFields.name,
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: ETechnologyTableTitles.band4g,
		accessorKey: ETechnologyTableFields.band4g,
		enableSorting: false,
		isHidden: technology === ETechnologiesValues.C_5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.title} />;
		},
	},
	{
		header: ETechnologyTableTitles.band5g,
		accessorKey: ETechnologyTableFields.band5g,
		isHidden: technology !== ETechnologiesValues.C_5G,
		enableSorting: false,
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const value = getValue();
			const from = original.band5g?.frequencyRange?.from;
			const to = original.band5g?.frequencyRange?.to;

			return <AdaptiveTableCell text={original.band5g ? `${value?.title} (${from} - ${to})` : value?.title} />;
		},
	},
	{
		header: ETechnologyTableTitles.antennaModel,
		accessorKey: ETechnologyTableFields.antennaModel,
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.title} />;
		},
	},
	{
		header: ETechnologyTableTitles.height,
		accessorKey: ETechnologyTableFields.height,
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: ETechnologyTableTitles.azimuth,
		accessorKey: ETechnologyTableFields.azimuth,
		size: columnSizes.size100,
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: ETechnologyTableTitles.mechanicalTilt,
		accessorKey: ETechnologyTableFields.mechanicalTilt,
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: ETechnologyTableTitles.electricalTilt,
		accessorKey: ETechnologyTableFields.electricalTilt,
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: ETechnologyTableTitles.totalTilt,
		accessorKey: ETechnologyTableFields.totalTilt,
		enableSorting: false,
		isHidden: technology === ETechnologiesValues.C_5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: ETechnologyTableTitles.earfcn,
		accessorKey: ETechnologyTableFields.earfcn,
		enableSorting: false,
		isHidden: technology === ETechnologiesValues.C_5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: ETechnologyTableTitles.tac,
		accessorKey: ETechnologyTableFields.tac,
		enableSorting: false,
		isHidden: technology === ETechnologiesValues.C_5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: ETechnologyTableTitles.pci,
		accessorKey: ETechnologyTableFields.pci,
		enableSorting: false,
		isHidden: technology === ETechnologiesValues.C_5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: ETechnologyTableTitles.mechanicalTiltBracket,
		accessorKey: ETechnologyTableFields.mechanicalTiltBracket,
		size: columnSizes.size170,
		enableSorting: false,
		isHidden: technology !== ETechnologiesValues.C_5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return isBoolean(value) && (
				<AdaptiveTableCell text={value ? 'yes' : 'no'} />
			);
		},
	},
	{
		header: ETechnologyTableTitles.mechanicalTiltBracketMount,
		accessorKey: ETechnologyTableFields.mechanicalTiltBracketMount,
		enableSorting: false,
		isHidden: technology !== ETechnologiesValues.C_5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: ETechnologyTableTitles.spinningRange,
		accessorKey: ETechnologyTableFields.spinningRange,
		enableSorting: false,
		isHidden: technology !== ETechnologiesValues.C_5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: ETechnologyTableTitles.nrarfcn,
		accessorKey: ETechnologyTableFields.nrarfcn,
		enableSorting: false,
		isHidden: technology !== ETechnologiesValues.C_5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.toString()} />;
		},
	},
	{
		header: 'Actions',
		accessorKey: 'actions',
		enableSorting: false,
		size: columnSizes.size90,
		cell: ({ row }: CellContext<any, any>) => {
			const handleClear = () => {
				clearCell(row?.original?.cellId);
				openClearCellModal();
			};

			const handleEdit = () => {
				editCell(row?.original);
				openEditCellModal();
			};

			return (
				<Actions
					clearHandler={handleClear}
					clearTooltip='Clear changeable fields in the cell'
					editHandler={handleEdit}
					editTooltip='Edit cell'
					gridNumberOfColumns={2}
					isClear
					isClearDisable={!isEditAccess}
					isEdit
					isEditDisable={!isEditAccess}
				/>);
		},
	},
]);
